import React, { memo, useEffect, useReducer, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { objects, validators } from "investira.sdk";
import services from "../services";
import withMessage from "../hoc/withMessage";
import withResponseHandling from "../hoc/withResponseHandling";
import { useSelector, useDispatch } from "react-redux";
import { acCarteiraSelected } from "../store/actions";

// Context
const CarteirasContext = React.createContext();
const initialState = {
  posicoes: [],
  carteira: "",
  carteira_id: null,
  carteira_seuid: null,
  carteira_tipo_id: "T",
  data_posicao: null,
  datas_posicoes: [],
  isDialogOpen: false,
  dialogType: null,
  posicaoSelected: {},
  posicaoTotal: 0,
  conferenciaCustodianteDatas: []
};

// Reducers
const reducers = (state = initialState, action) => {
  if (action.type === "SIMULACAO_RESETED") {
    return initialState;
  }
  if (action.type === "CARTEIRA_CHANGED") {
    return {
      ...state,
      ...action.payload,
      ...(state.data_posicao && {
        data_posicao: state.data_posicao
      })
    };
  }
  if (action.type === "CONFERENCIA_CUSTODIANTE_CHANGED") {
    return {
      ...state,
      conferenciaCustodianteDatas: action.payload
    };
  }
  if (action.type === "DATA_POSICAO_CHANGED") {
    return {
      ...state,
      data_posicao: action.payload
    };
  }
  if (action.type === "DATAS_POSICOES_CHANGED") {
    return {
      ...state,
      datas_posicoes: action.payload
    };
  }
  if (action.type === "POSICOES_CHANGED") {
    return {
      ...state,
      posicoes: action.payload
    };
  }
  if (action.type === "NEW_POSICAO_CHANGED") {
    const xNewPos = [...state.posicoes].concat(action.payload);
    return {
      ...state,
      posicoes: xNewPos
    };
  }
  if (action.type === "POSICAO_OPENED") {
    return {
      ...state,
      isDialogOpen: true,
      dialogType: "new"
    };
  }
  if (action.type === "POSICAO_CANCELED") {
    return {
      ...state,
      isDialogOpen: false,
      dialogType: null,
      posicaoSelected: {}
    };
  }
  if (action.type === "POSICAO_SELECTED") {
    return {
      ...state,
      posicaoSelected: action.payload
    };
  }
  if (action.type === "CARTEIRA_EDIT_OPENED") {
    return {
      ...state,
      isDialogOpen: true,
      dialogType: "carteira"
    };
  }
  if (action.type === "CARTEIRA_EDIT_CANCELED") {
    return {
      ...state,
      isDialogOpen: false,
      dialogType: null
    };
  }
  if (action.type === "TIPO_EDIT_OPENED") {
    return {
      ...state,
      isDialogOpen: true,
      dialogType: "tipo"
    };
  }
  if (action.type === "TIPO_EDIT_CANCELED") {
    return {
      ...state,
      isDialogOpen: false,
      dialogType: null
    };
  }
  if (action.type === "CLONE_POSICAO_OPENED") {
    return {
      ...state,
      isDialogOpen: true,
      dialogType: "clone"
    };
  }
  if (action.type === "CLONE_POSICAO_CANCELED") {
    return {
      ...state,
      isDialogOpen: false,
      dialogType: null
    };
  }
  if (action.type === "DATA_POSICAO_OPENED") {
    return {
      ...state,
      isDialogOpen: true,
      dialogType: "posicao"
    };
  }
  if (action.type === "DATA_POSICAO_CANCELED") {
    return {
      ...state,
      isDialogOpen: false,
      dialogType: null
    };
  }
  if (action.type === "POSICAO_TOTAL_CHANGED") {
    return {
      ...state,
      posicaoTotal: action.payload
    };
  }
};

// Dispatchs

const acSimulacaoReset = pString => ({
  type: "SIMULACAO_RESETED",
  payload: pString
});
const acUpdateCarteira = pObjCarteira => ({
  type: "CARTEIRA_CHANGED",
  payload: pObjCarteira
});
const acUpdateConferenciaCustodiante = pArray => ({
  type: "CONFERENCIA_CUSTODIANTE_CHANGED",
  payload: pArray
});
const acAddPosicao = pArray => ({
  type: "NEW_POSICAO_CHANGED",
  payload: pArray
});
const acUpdateDataPosicao = pDate => ({
  type: "DATA_POSICAO_CHANGED",
  payload: pDate
});
const acUpdateDatasPosicoes = pDate => ({
  type: "DATAS_POSICOES_CHANGED",
  payload: pDate
});
const acUpdatePosicoes = pArray => ({
  type: "POSICOES_CHANGED",
  payload: pArray
});
const acUpdateNewPosicaoOpened = () => ({
  type: "POSICAO_OPENED"
});
const acUpdateNewPosicaoCanceled = () => ({
  type: "POSICAO_CANCELED"
});
const acEditCarteiraOpened = () => ({
  type: "CARTEIRA_EDIT_OPENED"
});
const acEditCarteiraCanceled = () => ({
  type: "CARTEIRA_EDIT_CANCELED"
});
const acEditTipoOpened = () => ({
  type: "TIPO_EDIT_OPENED"
});
const acEditTipoCanceled = () => ({
  type: "TIPO_EDIT_CANCELED"
});
const acUpdatePosicaoSelected = pObjPosicao => ({
  type: "POSICAO_SELECTED",
  payload: pObjPosicao
});
const acUpdateClonePosicaoOpened = () => ({
  type: "CLONE_POSICAO_OPENED"
});
const acUpdateClonePosicaoCanceled = () => ({
  type: "CLONE_POSICAO_CANCELED"
});
const acEditDataPosicaoOpened = () => ({
  type: "DATA_POSICAO_OPENED"
});
const acEditDataPosicaoCanceled = () => ({
  type: "DATA_POSICAO_CANCELED"
});
const acPosicaoTotalChanged = pInt => ({
  type: "POSICAO_TOTAL_CHANGED",
  payload: pInt
});

// Custom Hook
export const useCarteirasContext = () => React.useContext(CarteirasContext);
export const CarteirasProvider = memo(props => {
  const location = useLocation();
  const navigate = useNavigate();
  const storeCarteiraCurrent = useSelector(store => store.carteiras.current);
  const storeDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducers, initialState);
  const handleReset = () => {
    dispatch(acSimulacaoReset());
  };
  const handleSetDataPosicao = pDate => {
    dispatch(acUpdateDataPosicao(pDate));
  };
  const handleSetDatasPosicoes = pDates => {
    dispatch(acUpdateDatasPosicoes(pDates));
  };
  const handleNewPosicao = () => {
    dispatch(acUpdateNewPosicaoOpened());
  };
  const handleNewPosicaoCancel = () => {
    //services.ativos.cancel.list.cancel();
    dispatch(acUpdateNewPosicaoCanceled());
  };
  const handleModifyCarteira = () => {
    dispatch(acEditCarteiraOpened());
  };
  const handleModifyCarteiraCancel = () => {
    dispatch(acEditCarteiraCanceled());
  };
  const handleModifyConferenciaCustodiante = pArray => {
    dispatch(acUpdateConferenciaCustodiante(pArray));
  };
  const handleModifyTipo = () => {
    dispatch(acEditTipoOpened());
  };
  const handleModifyTipoCancel = () => {
    dispatch(acEditTipoCanceled());
  };
  const handleClonePosicao = () => {
    dispatch(acUpdateClonePosicaoOpened());
  };
  const handleClonePosicaoCancel = () => {
    dispatch(acUpdateClonePosicaoCanceled());
  };
  const handleDataPosicao = () => {
    dispatch(acEditDataPosicaoOpened());
  };
  const handleDataPosicaoCancel = () => {
    dispatch(acEditDataPosicaoCanceled());
  };
  const handleUpdateCarteira = pObjCarteira => {
    dispatch(acUpdateCarteira(pObjCarteira));
  };
  const handleUpdatePosicaoTotal = pInt => {
    dispatch(acPosicaoTotalChanged(pInt));
  };
  const handleEditCarteira = pCarteira => {
    handleUpdateCarteira(pCarteira);
    storeDispatch(acCarteiraSelected(pCarteira));
    navigate(`/carteiras/${pCarteira.carteira_id}`);
  };
  const handleBulkAddPosicoes = (pPosicoes = []) => {
    dispatch(acUpdatePosicoes(pPosicoes));
  };
  const handleAddPosicoes = pObjPosicao => {
    //const xId = uuidv4();
    const xNovaPosicao = [{
      // posicao_id: xId,
      ...pObjPosicao
    }];
    dispatch(acAddPosicao(xNovaPosicao));
  };
  const handleRemovePosicoes = useCallback(async pObjPosicao => {
    const {
      carteira_id,
      ativo_id,
      data,
      ativo_tipo_id
    } = pObjPosicao;
    let xPosicoes = objects.deepCopy(state.posicoes);
    xPosicoes = xPosicoes.filter(x => {
      const xToDelete = x.ativo_id === ativo_id && x.carteira_id === carteira_id && x.data === data && x.ativo_tipo_id === ativo_tipo_id;
      return !xToDelete;
    });
    await deleteAtivo({
      data,
      carteira_id,
      ativo_id
    }, _rRes => {
      dispatch(acUpdatePosicoes(xPosicoes));
    }, rErr => {
      props.responseErrorHandling(rErr);
    });
  }, [state.posicoes]);
  const handleBulkRemovePosicoes = pPosicoes => {
    const xPromises = Object.values(pPosicoes).map(xPosicao => {
      const {
        carteira_id,
        ativo_id,
        data
      } = xPosicao;
      return deleteAtivo({
        data,
        carteira_id,
        ativo_id
      });
    });
    Promise.all(xPromises).then(rRes => {
      props.onMessageSuccess(`Posição excluída com sucesso`);
      let xPosicoes = objects.deepCopy(state.posicoes);
      xPosicoes = xPosicoes.filter(xPosicao => {
        return !Object.values(pPosicoes).some(pPosicao => {
          return xPosicao.ativo_id === pPosicao.ativo_id && xPosicao.carteira_id === pPosicao.carteira_id && xPosicao.data === pPosicao.data && xPosicao.ativo_tipo_id === pPosicao.ativo_tipo_id;
        });
      });
      dispatch(acUpdatePosicoes(xPosicoes));
    }).catch(rErr => {
      props.onMessageError(`Ocorreu uma falha durante a exclusão da posição`);
    });

    // const { carteira_id, ativo_id, data, ativo_tipo_id } = pObjPosicao;
    // let xPosicoes = objects.deepCopy(state.posicoes);

    // xPosicoes = xPosicoes.filter((x) => {
    //   const xToDelete =
    //     x.ativo_id === ativo_id &&
    //     x.carteira_id === carteira_id &&
    //     x.data === data &&
    //     x.ativo_tipo_id === ativo_tipo_id;

    //   console.log("xToDelete", xToDelete);
    //   return !xToDelete;
    // });

    // await deleteAtivo(
    //   { data, carteira_id, ativo_id },
    //   (_rRes) => {
    //     dispatch(acUpdatePosicoes(xPosicoes));
    //   },
    //   (rErr) => {
    //     props.responseErrorHandling(rErr);
    //   }
    // );
  };
  const handleUpdatePosicoes = pData => {
    const xIndex = state.posicoes.findIndex(pObjeto => {
      const xToUpdate = pObjeto.ativo_id === pData.ativo_id && pObjeto.carteira_id === pData.carteira_id && pObjeto.data === pData.data;
      return xToUpdate;
    });
    if (xIndex !== -1) {
      let xPosicoes = objects.deepCopy(state.posicoes);
      xPosicoes[xIndex] = {
        ...xPosicoes[xIndex],
        pData
      };
      dispatch(acUpdatePosicoes(xPosicoes));
    }
  };
  const handleEditPosicao = pObjeto => {
    dispatch(acUpdatePosicaoSelected(pObjeto));
    dispatch(acUpdateNewPosicaoOpened());
  };
  const handleMergePosicoes = (pData, pIsUpdate) => {
    if (pIsUpdate) {
      handleUpdatePosicoes(pData);
    } else {
      handleAddPosicoes(pData);
    }
  };
  function readPosicoes(pParams, pResolve, pReject, pFinally) {
    services.posicoes.list({
      ...pParams,
      sort: "ativo_tipo_id ASC, ativo ASC"
    }, rRes => {
      //dispatch(acUpdatePosicoes(rRes.data));
      pResolve && pResolve(rRes);
    }, rErr => {
      props.onMessageError(rErr.message);
      pReject && pReject(rErr);
    }, () => {
      pFinally && pFinally();
    });
  }
  function readCarteira(pCarteiraId, pResolve, pReject, pFinally) {
    services.carteiras.read(pCarteiraId, rRes => {
      handleUpdateCarteira(rRes.data[0]);
      pResolve && pResolve();
    }, rErr => {
      props.responseErrorHandling(rErr);
      pReject && pReject();
    }, () => {
      pFinally && pFinally();
    });
  }
  function readDatasPosicoes(pCarteiraId, pResolve, pReject, pFinally) {
    services.posicoes.datas({
      sort: "data DESC",
      carteira_id: pCarteiraId
    }, rRes => {
      pResolve && pResolve(rRes);
    }, rErr => {
      pReject && pReject(rErr);
    }, () => {
      pFinally && pFinally();
    });
  }
  function readConferenciaCustodianteDatas(pCarteiraId, pResolve, pReject, pFinally) {
    services.conferenciasCustodiantes.datas({
      sort: "data DESC",
      carteira_id: pCarteiraId
    }, rRes => {
      pResolve && pResolve(rRes);
    }, rErr => {
      pReject && pReject(rErr);
    }, () => {
      pFinally && pFinally();
    });
  }
  function createCarteira(pData = {}, pResolve, pReject, pFinally) {
    services.carteiras.create(pData, rRes => {
      props.onMessageSuccess("Carteira criada.");
      handleEditCarteira(rRes.data);
    }, rErr => {
      props.onMessageError(rErr.message);
    }, () => {
      pFinally && pFinally();
    });
  }
  function addAtivo(pAtivo = {}, pResolve, pReject, pFinally) {
    services.posicoes.add(pAtivo, rRes => {
      return pResolve && pResolve(rRes);
    }, rErr => {
      pReject && pReject(rErr);
    }, () => {
      pFinally && pFinally();
    });
  }
  function updateAtivo(pAtivo = {}, pResolve, pReject, pFinally) {
    return services.posicoes.update(pAtivo, rRes => {
      return pResolve && pResolve(rRes);
    }, rErr => {
      return rErr;
    }, () => {
      pFinally && pFinally();
    });
  }
  function savePosicoes(pData, pResolve, pReject, pFinally) {
    const xPromises = Object.values(pData.ativos).map(xAtivo => {
      return addAtivo({
        ...xAtivo,
        carteira_id: pData.carteira_id
      });
    });
    Promise.all(xPromises).then(rRes => {
      pResolve && pResolve(rRes);
      props.onMessageSuccess(`Posição copiada com sucesso`);
    }).catch(rErr => {
      pReject && pReject(rErr);
      props.onMessageError(`Ocorreu uma falha durante copia da posição`);
    }).finally(() => {
      pFinally && pFinally();
    });
  }
  function clonePosicao(pData, pResolve, pReject, pFinally) {
    services.posicoes.copy(pData, rRes => {
      const {
        carteira_id,
        data
      } = rRes.data;
      handleSetDataPosicao(data);
      readPosicoes({
        carteira_id,
        data
      }, rRes => {
        handleBulkAddPosicoes(rRes.data);
        pResolve && pResolve(rRes);
      });
      readDatasPosicoes(carteira_id, rRes => {
        handleSetDatasPosicoes(rRes.data);
      });
    }, rErr => {
      return pReject && pReject(rErr);
    }, () => {
      pFinally && pFinally();
    });
  }
  function updatePosicoes(pDate, pResolve, pReject, pFinally) {
    const xPromises = state.posicoes.map(xPosicao => {
      return updateAtivo({
        ...xPosicao,
        data: pDate
      });
    });
    Promise.all(xPromises).then(rRes => {
      pResolve && pResolve(rRes);
      handleSetDataPosicao(pDate);
      props.onMessageSuccess(`Posição atualiza com sucesso`);
      dispatch(acUpdatePosicoes(rRes));
    }).catch(rErr => {
      pReject && pReject(rErr);
      props.onMessageError(`Ocorreu um erro durante a atualização posição`);
    }).finally(() => {
      pFinally && pFinally();
    });
  }
  async function deleteAtivo(pPosicaoId, pResolve, pReject, pFinally) {
    await services.posicoes.remove(pPosicaoId, rRes => {
      pResolve && pResolve(rRes);
    }, rErr => {
      pReject && pReject(rErr);
    }, () => {
      pFinally && pFinally();
    });
  }
  function deletePosicoes(pData, pResolve, pReject, pFinally) {
    services.posicoes.exclude(pData, rRes => {
      const {
        carteira_id,
        data
      } = pData;
      handleSetDataPosicao(data);
      readPosicoes({
        carteira_id,
        data
      }, rRes => {
        handleBulkAddPosicoes(rRes.data);
      });
      pResolve && pResolve(rRes);
    }, rErr => {
      pReject && pReject(rErr);
    }, () => {
      pFinally && pFinally();
    });
  }
  function modifyCarteira(pData, pResolve, pReject, pFinally) {
    services.carteiras.modify(pData, rRes => {
      handleUpdateCarteira(rRes.data);
      pResolve && pResolve(rRes);
    }, rErr => {
      pReject && pReject(rErr);
    }, pFinally);
  }
  function somaPosicao(pPosicao) {
    let xResult = pPosicao.reduce((xAcc, xPosicao) => {
      return xAcc + Math.round(xPosicao.financeiro * 1e7) / 1e7;
    }, 0);
    return xResult;
  }
  useEffect(() => {
    if (location.pathname === "/carteiras/carteiras" || location.pathname === "/carteiras") {
      handleReset();
    }
  }, [location.pathname]);

  // Este trecho abaixo estava comentado por algun motivo

  // useEffect(() => {
  //   if (!validators.isNull(storeCarteiraCurrent.data_posicao)) {
  //     handleSetDataPosicao(storeCarteiraCurrent.data_posicao);
  //   }
  // }, []);

  useEffect(() => {
    if (validators.isNull(state.carteira_id) && validators.isNull(state.data_posicao)) {
      const xDataPosicao = storeCarteiraCurrent.data_posicao;
      handleSetDataPosicao(xDataPosicao);
      handleUpdateCarteira(storeCarteiraCurrent);
    }
  }, [state.carteira_id, state.data_posicao]);
  useEffect(() => {
    const xTotal = somaPosicao(state.posicoes);
    handleUpdatePosicaoTotal(xTotal);
  }, [state.posicoes]);
  useEffect(() => {
    if (!validators.isNull(state.carteira_id)) {
      readConferenciaCustodianteDatas(state.carteira_id, rRes => {
        const xCarteira = rRes?.data[`_${state.carteira_id}`];
        if (xCarteira) {
          handleModifyConferenciaCustodiante(xCarteira.datas);
        }
      });
    }
  }, [state.carteira_id]);
  return <CarteirasContext.Provider value={{
    state: {
      ...state,
      data_posicao: state.data_posicao || storeCarteiraCurrent.data_posicao,
      carteira_id: state.carteira_id || storeCarteiraCurrent.carteira_id
    },
    actions: {
      handleSetDataPosicao,
      handleNewPosicao,
      handleNewPosicaoCancel,
      handleUpdateCarteira,
      handleEditCarteira,
      handleAddPosicoes,
      handleRemovePosicoes,
      handleUpdatePosicoes,
      handleEditPosicao,
      handleMergePosicoes,
      handleClonePosicao,
      handleClonePosicaoCancel,
      handleDataPosicao,
      handleSetDatasPosicoes,
      handleDataPosicaoCancel,
      handleBulkAddPosicoes,
      handleReset,
      readDatasPosicoes,
      readPosicoes,
      createCarteira,
      addAtivo,
      updatePosicoes,
      deleteAtivo,
      deletePosicoes,
      handleBulkRemovePosicoes,
      savePosicoes,
      clonePosicao,
      readCarteira,
      handleModifyCarteira,
      handleModifyCarteiraCancel,
      handleModifyTipo,
      handleModifyTipoCancel,
      modifyCarteira,
      updateAtivo
    }
  }}>
      {props.children}
    </CarteirasContext.Provider>;
});
CarteirasProvider.displayName = "CarteirasProvider";
export default withMessage(withResponseHandling(CarteirasProvider));
